/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useReactToPrint } from 'react-to-print';
import PropTypes from 'prop-types';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import './PointOfSale.css';
import MDSnackbar from 'components/MDSnackbar';

const initAttr = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  customer_id: '',
  quotation_id: '',
  vat_id: '2',
  status: 'edit',
  branch_receive: '',
  credit_day: '',
  credit_money: '',
  print_url: '',
  print_no: '0',
  detail_no: '0',
  created_at: '',
  updated_at: '',
  print: '',
  quotation: '',
  payment_type: 'CASH',
  shipping_id: '',
  time_shipping: '00:00',
  car_id: '',
  brandsend_id: '',
  printso_no: '',
  address: '',
  address2: '',
  printinvoice_no: '',
  discount_unit: 'Y',
  date_record: Func.dateNow(),
  date_due: Func.dateNow(),
  date_shipping: Func.dateNow(),
  qr_bottom_bill_text: '',
  qr_bottom_bill_img: '',
};

const PosPrint = forwardRef(({ paymentType }, ref) => {
  const mod = 'pointofsale';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [campaign, setCampaign] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);
  const promiseResolveRef = useRef(null);
  const [notify, setNotify] = useState(null);

  const quantityDisplay = (num) => {
    const fl = Math.floor(num);
    if (fl === num) return Func.numberDisplay(num, 0);
    return Func.numberDisplay(num);
  };

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);
  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false);
    },
  });

  const print = (inp, isSetState = true) => {
    setLoading(true);

    if (isSetState) {
      const status = JSON.parse(localStorage.getItem('pos_status'));
      localStorage.setItem(
        'pos_status',
        JSON.stringify({ ...status, step: 'print', status: 'fetching' })
      );
    }

    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      id: inp,
      lang: app.lang,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setList(result.list);
          setCampaign(result.campaign);
          handlePrint();

          if (isSetState) {
            const status = JSON.parse(localStorage.getItem('pos_status'));
            if (status?.step === 'print' && status?.status === 'fetching') {
              localStorage.setItem(
                'pos_status',
                JSON.stringify({ ...status, step: 'print', status: 'success' })
              );
            } else {
              showNotify('เกิดข้อผิดพลาดในการบันทึกสถานะ Local', 'error');
            }
          }
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const paymentTypeDisplay = () => {
    if (attr.payment_type === 'CASH') return 'เงินสด';
    if (attr.payment_type === 'TRANSFER') return 'เงินโอน';
    if (attr.payment_type === 'CREDIT') return `บัตร ${attr.card_name}`;
    if (attr.payment_type === 'CASH_TRANSFER_CREDIT')
      return `เงินสด/เงินโอน/บัตร ${attr.card_name}`;
    if (attr.payment_type === 'CASH_TRANSFER') return 'เงินสด/เงินโอน';
    if (attr.payment_type === 'CASH_CREDIT') return `เงินสด/บัตร ${attr.card_name}`;
    if (attr.payment_type === 'TRANSFER_CREDIT') return `เงินโอน/บัตร ${attr.card_name}`;
    return '';
  };

  const renderHeader = () => {
    return (
      <table width='100%'>
        <tbody>
          <tr>
            <td align='center'>
              {attr.image === '' ? null : <img src={attr.image} alt='' width='130' />}
            </td>
          </tr>
          <tr>
            <td align='center'>{attr.branch_name}</td>
          </tr>
          <tr>
            <td align='center'>
              <div className='title'>RECEIPT</div>
            </td>
          </tr>
          <tr>
            <td>
              <p>&nbsp;</p>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderOrder = () => {
    return (
      <table width='100%' cellPadding={5}>
        <thead>
          <tr>
            <th width='10%'>QTYL</th>
            <th>&nbsp; ITEM</th>
            <th align='right' width='20%'>
              PRICE
            </th>
            <th align='right' width='20%'>
              TOTAL
            </th>
          </tr>
        </thead>
        <tbody>
          {list.map((item) => (
            <tr key={item.id}>
              <td align='center' valign='top'>
                {quantityDisplay(item.quantity)}
              </td>
              <td valign='top'>{item.name}</td>
              <td align='right' valign='top'>
                {Func.numberDisplay(item.price, 2)}
              </td>
              <td align='right' valign='top'>
                {Func.numberDisplay(item.total, 2)}
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={4}>&nbsp;</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderCampaign = () => {
    return (
      <table width='100%' cellPadding={5}>
        <tbody>
          {campaign.map((item) => (
            <tr key={item.id}>
              <td valign='top'>** {item.promotion_name}</td>
              <td align='right' valign='top'>
                {item.discount * 1 > 0 ? `-${Func.numberDisplay(item.discount, 2)}` : ''}
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={2}>&nbsp;</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderOrder2 = () => {
    return (
      <Grid container spacing={1}>
        <Grid item xs={1} align='right'>
          QTYL
        </Grid>
        <Grid item xs={7}>
          ITEM
        </Grid>
        <Grid item xs={2} align='right'>
          PRICE
        </Grid>
        <Grid item xs={2} align='right'>
          TOTAL
        </Grid>
        <Grid item xs={12}>
          {list.map((item) => (
            <Grid container spacing={1}>
              <Grid item xs={1} align='right'>
                {quantityDisplay(item.quantity)}
              </Grid>
              <Grid item xs={7}>
                {item.name}
              </Grid>
              <Grid item xs={2} align='right'>
                {Func.numberDisplay(item.price, 2)}
              </Grid>
              <Grid item xs={2} align='right'>
                {Func.numberDisplay(item.amount, 2)}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    );
  };

  const renderTotal = () => {
    return (
      <table width='100%'>
        <thead>
          <tr>
            <td>PRICE BEFORE DISCOUNT</td>
            <td align='right'>{Func.numberDisplay(attr.total, 2)}</td>
          </tr>
        </thead>
      </table>
    );
  };

  const renderNetTotal = () => {
    return (
      <table width='100%'>
        <thead>
          <tr>
            <th align='left' className='h2'>
              <h3>TOTAL</h3>
            </th>
            <th align='right' className='h2'>
              <h3>{Func.numberDisplay(attr.nettotal, 2)}</h3>
            </th>
          </tr>
        </thead>
      </table>
    );
  };

  const renderSummary = () => {
    return (
      <table width='100%'>
        <thead>
          <tr>
            <td width='70%'>เงินสด</td>
            <td align='right'>{Func.numberDisplay(attr.money_receive, 2)}</td>
          </tr>
          <tr>
            <td align='left'>เงินทอน</td>
            <td align='right'>{Func.numberDisplay(attr.money_change, 2)}</td>
          </tr>
        </thead>
      </table>
    );
  };

  const renderFooter = () => {
    return (
      <table width='100%'>
        <tbody>
          <tr>
            <td>รวมรายการ {attr.count_list}</td>
            <td>รวมชิ้น {Func.numberDisplay(attr.quantity_list)}</td>
          </tr>
          <tr>
            <td colSpan={2}>Cashier : {attr.user_name}</td>
          </tr>
          <tr>
            <td colSpan={2}>การชำระเงิน : {paymentTypeDisplay()}</td>
          </tr>
          <tr>
            <td>{attr.code}</td>
            <td>{Func.dateTimeDisplay(attr.today)}</td>
          </tr>
          <tr>
            <td colSpan={2}>
              <br />
            </td>
          </tr>
          <tr>
            <td>
              <h1>Contact us</h1>
              <h1>Scan here</h1>
            </td>
            <td align='center'>
              <img
                src='https://changsiamthailand.com/img/qrcode-followus.png'
                width='60'
                alt='Follow us'
              />
            </td>
          </tr>
          {attr.qr_bottom_bill_text && attr.qr_bottom_bill_img && (
            <>
              <tr>
                <td>&nbsp;</td>
              </tr>
              <tr>
                <td>&nbsp;</td>
              </tr>
              <tr>
                <td align='center' colSpan={2}>
                  <h1>{attr.qr_bottom_bill_text}</h1>
                </td>
              </tr>
              <tr>
                <td align='center' colSpan={2}>
                  <img src={attr.qr_bottom_bill_img} width='90' alt='qr_bottom_bill' />
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderPrint = () => {
    return (
      <div style={{ display: 'none' }}>
        <div ref={printRef} className='slip'>
          {renderHeader()}
          {renderOrder()}
          {renderTotal()}
          {renderCampaign()}
          {renderNetTotal()}
          {renderSummary()}
          {renderFooter()}
          <br />.
        </div>
      </div>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      print,
    };
  });

  return (
    <div>
      {renderLoading()}
      {renderPrint()}
      {notify}
    </div>
  );
});

PosPrint.propTypes = {
  paymentType: PropTypes.string.isRequired,
};

export default PosPrint;
